import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/States";
import { NewSearchDialog } from "@/components/stage1/NewSearchDialog";
import { ResultsTable } from "@/components/stage1/ResultsTable";
import { useFetch } from "@/lib/hooks";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

function SearchCard({ s, active, onClick, onDelete, canDelete }) {
  const b = s.by_status || {};
  return (
    <button type="button" onClick={onClick} data-testid={`stage1-search-${s.id}`}
      className={`w-full rounded-lg border p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "hover:bg-accent/50"}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium leading-snug">{s.name}</p>
        {canDelete && <span role="button" tabIndex={0} aria-label="Delete search" onClick={(e) => { e.stopPropagation(); onDelete(s); }} data-testid={`stage1-search-delete-${s.id}`}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></span>}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">SIC {s.sic_codes.join(", ")} · {s.radius ? `${s.radius} ${s.radius_unit} of ` : ""}{s.location}</p>
      {s.status === "running" ? <p className="mt-1 flex items-center gap-1 text-xs"><Loader2 className="h-3 w-3 animate-spin" />Searching Companies House…</p>
        : s.status === "failed" ? <p className="mt-1 text-xs text-destructive">{s.error}</p>
        : <p className="mt-1 text-xs" data-testid={`stage1-search-counts-${s.id}`}><b>{Object.values(b).reduce((a, n) => a + n, 0)}</b> companies · {b.researched || 0} in Companies · {b.not_matched || 0} not a match{s.cost_gbp ? ` · £${s.cost_gbp.toFixed(2)} spent` : ""}</p>}
      {s.status === "done" && s.directors_loaded < Object.values(b).reduce((a, n) => a + n, 0) &&
        <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground" data-testid={`stage1-directors-progress-${s.id}`}><Loader2 className="h-3 w-3 animate-spin" />Loading directors {s.directors_loaded}/{Object.values(b).reduce((a, n) => a + n, 0)}</p>}
    </button>
  );
}

export default function Stage1() {
  const { api, can } = useWorkspace();
  const { data, reload } = useFetch("/stage1/searches");
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const active = (data || []).find((s) => s.id === activeId) || (data || [])[0];
  const running = (data || []).some((s) => s.status === "running" || (s.status === "done" && s.directors_loaded < Object.values(s.by_status || {}).reduce((a, n) => a + n, 0)));
  useEffect(() => { if (!running) return undefined; const id = setInterval(reload, 6000); return () => clearInterval(id); }, [running, reload]);
  const changed = useCallback(() => reload(), [reload]);
  const del = async (s) => {
    if (!window.confirm(`Delete the search "${s.name}" and its results? Companies already moved into Companies are kept.`)) return;
    try { await api.delete(`/stage1/searches/${s.id}`); toast.success("Search deleted"); if (s.id === activeId) setActiveId(null); reload(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid="stage1-page">
      <PageHeader eyebrow="Companies House" title="Stage 1 search" description="Step 1: list every registered company in a trade near a town (free). Step 2: tick companies and research them; confirmed ones move into Companies."
        actions={can("write") && <Button onClick={() => setOpen(true)} data-testid="stage1-new-button"><Plus className="mr-1.5 h-4 w-4" />New search</Button>} />
      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <div className="space-y-2" data-testid="stage1-searches">
          {(data || []).map((s) => <SearchCard key={s.id} s={s} active={active?.id === s.id} onClick={() => setActiveId(s.id)} onDelete={del} canDelete={can("delete")} />)}
          {data && !data.length && <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="stage1-no-searches">No searches yet. Click New search to list companies by trade and area.</p>}
        </div>
        <div className="min-w-0">{active && active.status === "done" ? <ResultsTable key={active.id} search={active} onChanged={changed} /> : null}</div>
      </div>
      <NewSearchDialog open={open} onOpenChange={setOpen} onCreated={(s) => { setActiveId(s.id); reload(); }} />
    </div>
  );
}
